import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { playlistApi } from '../../api/swagger'
import { useAppSnackbar } from '../useAppSnackbar'
import useLoadingContext from '../useLoadingContext'
import { useLoadingIds } from '../useLoadingIds'

interface UseCopyPlaylistHookResponse {
  copyPlaylist: (playlistKey: number) => void
  newPlaylistKey: number | null
}

export const useCopyPlaylist = (): UseCopyPlaylistHookResponse => {
  const { t } = useTranslation()
  const { handleError, handleMessage } = useAppSnackbar()
  const [newPlaylistKey, setNewPlaylistKey] = useState<number | null>(null)

  /**
   * We use a ref instead of state because:
   * - The playlistKey is only needed for the async side effect.
   * - We do NOT want to trigger a re-render when it changes.
   * - The value must be immediately available before calling triggerFetch().
   *
   * Using state here could cause a "one render behind" issue since
   * state updates are asynchronous. The ref guarantees the latest value
   * is read inside copyPlaylistAsync without introducing extra renders.
   */
  const playlistKeyRef = useRef<number>()

  const {
    Playlist: {
      clonePlaylist: clonePlaylistLoadingId,
      fetchUserPlaylists: fetchUserPlaylistsLoadingId,
    },
  } = useLoadingIds()

  const defaultErrorMessage = t(
    'ContentDetailPlaylistCard.CopyPlaylist.Error',
    'An error occurred while copying the playlist.'
  )

  const copyPlaylistAsync = async () => {
    const playlistKey = playlistKeyRef.current
    if (!playlistKey) return

    try {
      const response = await playlistApi.clonePlaylist({ playlistKey })
      setNewPlaylistKey(response.playlistKey)

      handleMessage(
        t(
          'ContentDetailPlaylistCard.ClonePlaylist.Success',
          'The playlist was copied successfully.'
        )
      )
    } catch (error) {
      await handleError(error, defaultErrorMessage)
    }
  }

  const { triggerFetch, addLoadingIds } = useLoadingContext({
    asyncFunction: copyPlaylistAsync,
    loadingId: clonePlaylistLoadingId,
    callback: () => {
      addLoadingIds([fetchUserPlaylistsLoadingId])
    },
  })

  const copyPlaylist = (playlistKey: number) => {
    playlistKeyRef.current = playlistKey
    triggerFetch()
  }

  return { copyPlaylist, newPlaylistKey }
}
